import React, { Fragment } from 'react';
import R from 'ramda';
import PropTypes from 'prop-types';
import Select from '@cotyorg/ccx-atoms/Select';
import Button from '@cotyorg/ccx-atoms/Button';
import ProductSwatchColours from '@cotyorg/ccx-organism-product-swatch/components/ProductSwatchColours';
import ProductSwatchDropdown from '@cotyorg/ccx-organism-product-swatch/components/ProductSwatchDropdown';
import {
  trackSelectedShadeEvent,
  trackBuyEvent,
} from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/product';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import Link from '@cotyorg/ccx-atoms/Link';
import ProductSteps from '../../../ProductSteps';
import { isSmallBreakpoint } from '../../../../utils';
import { getCurrencyCode } from '../../../../molecules/shopping-bag/utils';
import ProductRetailers from '../../../ProductRetailers';
import { updateQueryStringWithoutReload } from './helpers';
import ShoppableBuyButton from '../../../Shoppable/components/ShoppableBuyButton';

const addQuantity = (basketProduct, quantity) => ({
  ...basketProduct,
  quantity,
});

const ProductSelection = ({
  productDetails,
  subcategoryTitle,
  basketProduct,
  retailerInfo,
  showModal,
  hideModal,
  addToBasket,
  setProductQuantity,
  productQuantity = 1,
  currentBreakpoint,
  swatches,
  categoryTitle,
  presentationLocale,
  customProductBuyLink,
  hidePrice,
}) => {
  const selectOptions = R.map((value) => ({
    name: `${value}`,
    value,
    isSelected: value === productQuantity,
  }))(Array.from({ length: 9 }, (value, index) => index + 1));

  // TODO
  // Centralize information about locales with basket information
  const isLocaleWithBasket = false;

  const isLocaleWithShoppable =
    presentationLocale === 'en-gb' && !customProductBuyLink;

  return (
    <div className="product-selection">
      <ProductSwatchColours
        label={R.prop('shadeSelectLabel', productDetails)}
        separator="/ "
        labelFirst
        nameFirst
        onChange={({ shade, sku }) => {
          updateQueryStringWithoutReload({ shade });

          trackSelectedShadeEvent({
            shade: R.toLower(shade),
            sku,
            name: R.compose(R.toLower(), R.prop('title'))(productDetails),
            type: R.toLower(subcategoryTitle),
            location: 'radio button',
          });
        }}
      />

      <div className="product-selection__swatch-dropdown">
        <ProductSwatchDropdown
          label={R.prop('shadeSelectLabel', productDetails)}
          separator="/ "
          labelFirst
          nameFirst
          chevronIconSrc="/ccx-files/assets/chevron-down.svg"
          onChange={({ shade, sku }) => {
            trackSelectedShadeEvent({
              shade: R.toLower(shade),
              sku,
              name: R.compose(R.toLower(), R.prop('title'))(productDetails),
              type: R.toLower(subcategoryTitle),
            });
          }}
        />
      </div>

      {isSmallBreakpoint({ currentBreakpoint }) && (
        <>
          {R.prop('descriptionHTML', productDetails) ? (
            <div
              className="product-info__description"
              dangerouslySetInnerHTML={{
                __html: R.prop('descriptionHTML', productDetails),
              }}
            />
          ) : (
            <div className="product-info__description">
              {R.prop('description', productDetails)}
            </div>
          )}

          <ProductSteps />
        </>
      )}

      <OptionalSection
        renderCondition={!!customProductBuyLink}
        section={() => (
          <Link
            extraClass="button button--large button--primary"
            link={customProductBuyLink}
            target="_blank"
          >
            {R.prop('buyButtonLabel', productDetails)}
          </Link>
        )}
      />

      <OptionalSection
        renderCondition={isLocaleWithShoppable}
        section={() => (
          <ShoppableBuyButton
            product={basketProduct}
            variation={false}
            buttonText={R.prop('buyButtonLabel', productDetails)}
            selectLabelText={R.prop('quantityLabel', productDetails)}
          />
        )}
      />

      <OptionalSection
        renderCondition={isLocaleWithBasket}
        section={() =>
          R.length(R.prop('retailers', basketProduct)) > 0 && (
            // R.prop('numberOfRetailers', retailerInfo) > 0 &&
            // R.prop('inStock', retailerInfo) &&
            <div className="grid-x">
              <div className="cell xsmall-16 small-8">
                <div className="product-selection__quantity">
                  <label
                    className="product-selection__quantity__label"
                    htmlFor="retailers-qty"
                  >
                    {R.prop('quantityLabel', productDetails)}
                    :
                    <Select
                      id="retailers-qty"
                      chevronIconSrc="/ccx-files/assets/chevron-down.svg"
                      selectOptions={selectOptions}
                      onChange={(value) =>
                        setProductQuantity(parseInt(value, 10))
                      }
                    />
                  </label>
                </div>
              </div>

              <div className="cell xsmall-16 small-8">
                <Button
                  testAttribute="add-to-basket-button"
                  theme="primary"
                  size="large"
                  extraClass="product-selection__button"
                  onClick={() => {
                    addToBasket(addQuantity(basketProduct, productQuantity));
                    trackBuyEvent({
                      currencyCode: getCurrencyCode({
                        retailers: R.prop('retailers', basketProduct),
                      }),
                      name: R.path(['retailers', '0', 'name'])(basketProduct),
                      link: R.path(['retailers', '0', 'deepLinkUrl'])(
                        basketProduct
                      ),
                      price: R.path(['retailers', '0', 'price'])(basketProduct),
                      productTitle: R.prop('name', basketProduct),
                      sku: R.prop('sku', basketProduct),
                      productSwatch: { swatches },
                      eventName: 'addToBasket',
                      subcategoryTitle,
                      categoryTitle,
                      productQuantity,
                      brand: 'Rimmel London',
                      list: 'Product page',
                    });
                    showModal();
                    setTimeout(() => {
                      hideModal();
                    }, 5000);
                  }}
                >
                  {R.prop('buyButtonLabel', productDetails)}
                </Button>
              </div>
            </div>
          )
        }
      />

      <OptionalSection
        renderCondition={!isLocaleWithBasket && !isLocaleWithShoppable}
        section={() => (
          <ProductRetailers
            productTitle={R.prop('title', productDetails)}
            categoryTitle={categoryTitle}
            subcategoryTitle={subcategoryTitle}
            productSwatch={{ swatches }}
            hidePrice={hidePrice}
          />
        )}
      />
      {R.prop('numberOfRetailers', retailerInfo) === 0 &&
        R.prop('retailersNotFoundLabel', productDetails)}
      {R.prop('numberOfRetailers', retailerInfo) > 0 &&
        !R.prop('inStock', retailerInfo) && (
          <Button size="large" theme="secondary" disabled>
            {R.prop('outOfStockLabel', productDetails)}
          </Button>
        )}
    </div>
  );
};

ProductSelection.propTypes = {
  productDetails: PropTypes.shape({
    averageRating: PropTypes.any,
    benefits: PropTypes.arrayOf(PropTypes.object),
    buyButtonLabel: PropTypes.string,
    bvid: PropTypes.string,
    description: PropTypes.string,
    fromRetailersLabel: PropTypes.string,
    howToSteps: PropTypes.arrayOf(PropTypes.object),
    showIngredients: PropTypes.string,
    hideIngredients: PropTypes.string,
    longDescription: PropTypes.string,
    noReviewsLabel: PropTypes.string,
    numberOfReviews: PropTypes.number,
    outOfStockLabel: PropTypes.string,
    priceRetailersPluralLabel: PropTypes.string,
    priceRetailersSingularLabel: PropTypes.string,
    quantityLabel: PropTypes.string,
    readReviewsLabel: PropTypes.string,
    retailersNotFoundLabel: PropTypes.string,
    shadeSelectLabel: PropTypes.string,
    title: PropTypes.string,
  }),
  subcategoryTitle: PropTypes.string,
  categoryTitle: PropTypes.string,
  basketProduct: PropTypes.shape({
    name: PropTypes.string,
    shadeName: PropTypes.string,
    price: PropTypes.number,
    currency: PropTypes.string,
    image: PropTypes.string,
    ean: PropTypes.string,
    retailers: PropTypes.array,
    sku: PropTypes.string,
    productId: PropTypes.string,
  }),
  retailerInfo: PropTypes.shape({
    numberOfRetailers: PropTypes.number,
    lowestPrice: PropTypes.shape({
      currencySymbol: PropTypes.string,
      number: PropTypes.number,
      price: PropTypes.string,
    }),
    inStock: PropTypes.bool,
  }),
  showModal: PropTypes.func,
  hideModal: PropTypes.func,
  addToBasket: PropTypes.func,
  productQuantity: PropTypes.number,
  setProductQuantity: PropTypes.func,
  currentBreakpoint: PropTypes.string,
  swatches: PropTypes.arrayOf(
    PropTypes.shape({
      sku: PropTypes.string,
      itemName: PropTypes.string,
      skuColor: PropTypes.string,
    })
  ),
  presentationLocale: PropTypes.string.isRequired,
  customProductBuyLink: PropTypes.string,
  hidePrice: PropTypes.bool,
};

export default ProductSelection;
